<template>
  <v-dialog v-model="show" max-width="560" persistent scrollable>
    <v-card>
      <v-card-title class="text-h6">
        <v-icon color="primary" class="mr-2">mdi-whatsapp</v-icon>
        Solicitar anamnese
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <section class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-clipboard-text</v-icon>
              <span>Anamneses</span>
            </div>
          </div>
          <p class="sa-lead">
            A paciente recebe as perguntas pelo WhatsApp e responde quando puder.
            As respostas caem no prontuário, no mesmo lugar das demais.
          </p>

          <v-select
            v-model="selecionadas"
            :items="itens"
            item-text="label"
            item-value="value"
            label="Quais anamneses enviar"
            multiple
            chips
            small-chips
            outlined
            dense
            :loading="carregando"
            :hide-details="selecionadas.length > 0"
            :error-messages="selecionadas.length ? [] : ['Escolha pelo menos uma.']"
            class="mb-3"
          />

          <!--
            O aviso e o ponto da salvia-833: esta acao tem efeito EXTERNO — manda
            mensagem para a paciente agora. Pelo assistente isso e Tier 2 (a IA
            propoe, o humano confirma). Aqui quem dispara ja e o humano, entao a
            confirmacao extra seria redundante; o que nao pode e o efeito ficar
            implicito. Por isso o texto, e nao um segundo dialogo.
          -->
          <v-alert dense text type="info" class="sa-aviso mb-0">
            Ao confirmar, a mensagem sai imediatamente para
            <strong>{{ patientName || "a paciente" }}</strong>.
            Se a conversa do WhatsApp estiver fechada, ela recebe primeiro o
            convite e as perguntas começam quando responder.
          </v-alert>
        </section>
      </v-card-text>

      <v-card-actions class="ds-dialog-actions">
        <v-btn outlined color="primary" :disabled="enviando" @click="show = false">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          depressed
          :disabled="!selecionadas.length"
          :loading="enviando"
          @click="solicitar"
        >
          <v-icon left small>mdi-send</v-icon>
          Solicitar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import http from "@/services/http";
import { listAnamneseTemplates } from "@/services/anamneseTemplateService";

/**
 * salvia-833: solicitar anamnese avulsa pelo prontuario, sem passar pelo
 * assistente e sem consulta marcada.
 *
 * Nao e feature nova de backend: o `/api/external-anamnese/initiate/{id}` ja
 * aceita a chamada sem os campos de consulta (todos `nullable`) — so nao tinha
 * quem o chamasse assim pela tela.
 *
 * O payload espelha DE PROPOSITO o do CalendarDialogAddNewEvent: fila unica de
 * tokens em `anamnese_type`, com as personalizadas como "tmpl:<id>". O criterio
 * de aceite da task e que as duas portas produzam o MESMO estado — e o jeito de
 * garantir isso nao e reimplementar o mapeamento aqui, e falar a mesma lingua.
 * (Mesma classe de problema da salvia-823 e da salvia-826: acao igual, portas
 * diferentes, estado gravado diferente.)
 */
export default {
  name: "SolicitarAnamneseDialog",

  props: {
    value: { type: Boolean, default: false },
    patientId: { type: [Number, String], required: true },
    patientName: { type: String, default: "" },
  },

  data() {
    return {
      // Mesma pre-selecao da outra porta: "Geral" e o caso comum.
      selecionadas: ["geral"],
      // Os 3 defaults sao fixos e vao por NOME, nao por id de template. Mandar
      // o id de um default faria a comunicacao rodar o fluxo do template em vez
      // do fluxo padrao — outro caminho para a mesma escolha.
      tiposPadrao: [
        { label: "Geral", value: "geral" },
        { label: "Nutricionista", value: "nutricionista" },
        { label: "Exames", value: "exames" },
      ],
      personalizadas: [],
      carregando: false,
      enviando: false,
    };
  },

  computed: {
    show: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },

    userData() {
      return this.$store.getters["auth/user"];
    },

    itens() {
      const customs = (this.personalizadas || []).map((t) => ({
        label: t.name,
        value: `tmpl:${t.id}`,
      }));
      return [...this.tiposPadrao, ...customs];
    },
  },

  watch: {
    value(aberto) {
      if (aberto) {
        this.selecionadas = ["geral"];
        this.carregarPersonalizadas();
      }
    },
  },

  methods: {
    async carregarPersonalizadas() {
      this.carregando = true;
      try {
        const lista = await listAnamneseTemplates();
        // So as ATIVAS e nao-default: as default ja estao em `tiposPadrao`.
        this.personalizadas = (lista || []).filter(
          (t) => t && t.active && !t.isDefault,
        );
      } catch (err) {
        console.error("Erro ao carregar anamneses personalizadas:", err);
        // Nao bloqueia: sem as personalizadas o medico ainda solicita as padrao.
        this.personalizadas = [];
      } finally {
        this.carregando = false;
      }
    },

    async solicitar() {
      if (!this.selecionadas.length) return;

      this.enviando = true;
      try {
        // Sem campos de `appointment_*`: e exatamente o ponto da task. O
        // `doctor_name` o backend preenche sozinho com o medico autenticado.
        const payload = {
          anamnese_type: this.selecionadas.join(","),
        };
        const doctorId = this.userData?.id;
        if (doctorId) payload.doctor_id = doctorId;

        await http.post(
          `/api/external-anamnese/initiate/${this.patientId}`,
          payload,
        );

        this.show = false;
        this.$emit("solicitada");
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          "Não foi possível solicitar a anamnese.";
        this.$emit("erro", msg);
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>

<style scoped>
.sa-lead {
  color: rgba(0, 0, 0, 0.7);
  font-size: 0.92rem;
  margin-bottom: 12px;
}
.sa-aviso {
  font-size: 0.86rem;
}
</style>
